import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate, fmtDateTime } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { RiskBadge, STAGES, STAGE_LABELS } from "./Badges";
import { SignaturePad } from "./SignaturePad";
import { EscalationTrail } from "./EscalationTrail";
import { PhotoUpload, Thumb } from "./PhotoUpload";

const Stepper = ({ stage }) => {
  const idx = STAGES.indexOf(stage);
  return (
    <div className="grid grid-cols-7 gap-1" data-testid="workflow-stepper">
      {STAGES.map((s, i) => (
        <div key={s} className="flex flex-col items-center gap-1 text-center">
          <div className={`flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-mono ${i < idx ? "border-emerald-500 bg-emerald-500/20 text-emerald-300" : i === idx ? "border-rose-500 bg-rose-600 text-white" : "border-slate-700 text-slate-500"}`}>
            {i < idx ? <Check className="h-3.5 w-3.5" /> : i + 1}
          </div>
          <span className={`text-[9px] leading-tight sm:text-[10px] ${i === idx ? "text-rose-300" : "text-slate-500"}`}>{STAGE_LABELS[s]}</span>
        </div>
      ))}
    </div>
  );
};

const AdvanceForm = ({ wo, onDone }) => {
  const { user, isManager } = useAuth();
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ note: "", risk: wo.risk, photos: [] });
  const next = STAGES[STAGES.indexOf(wo.stage) + 1];
  useEffect(() => {
    if (next === "assigned") api.get("/users").then((r) => setUsers(r.data.filter((u) => ["maintenance_manager", "contractor", "supervisor", "operator"].includes(u.role) && u.active && (u.site_ids || []).includes(wo.site_id))));
  }, [next, wo.site_id]);
  const allowed = isManager || (wo.assigned_to === user.id && next === "repair_completed");
  if (!next || !allowed || user.role === "auditor") return null;
  const submit = async () => {
    try {
      await api.post(`/workorders/${wo.id}/advance`, f);
      toast.success(`Moved to ${STAGE_LABELS[next]}`);
      onDone();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <div className="space-y-3 rounded-md border border-rose-500/30 bg-rose-500/5 p-4" data-testid="advance-form">
      <p className="eyebrow text-rose-300">Next step: {STAGE_LABELS[next]}</p>
      {next === "risk_assessment" && (
        <select className="field" value={f.risk} onChange={(e) => setF({ ...f, risk: e.target.value })} data-testid="advance-risk-select">
          <option value="high">High risk - escalates every 4h</option><option value="medium">Medium risk - escalates every 24h</option><option value="low">Low risk - planned maintenance</option>
        </select>
      )}
      {next === "assigned" && (
        <select className="field" value={f.assigned_to || ""} onChange={(e) => setF({ ...f, assigned_to: e.target.value })} data-testid="advance-assignee-select">
          <option value="">Select engineer / contractor...</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}{u.company ? ` (${u.company})` : ""}</option>)}
        </select>
      )}
      <textarea className="field h-20 py-2" placeholder="Notes" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="advance-note" />
      {["repair_completed", "verification", "closed"].includes(next) && <PhotoUpload value={f.photos} onChange={(photos) => setF({ ...f, photos })} testid="advance-photos" label="Evidence" />}
      {next === "verification" && <SignaturePad onChange={(signature) => setF({ ...f, signature })} testid="advance-signature" />}
      <Button onClick={submit} className="tap w-full bg-rose-600 hover:bg-rose-700" data-testid="advance-submit-btn">Confirm: {STAGE_LABELS[next]}</Button>
    </div>
  );
};

export const WorkOrderDetail = ({ id, onClose, onChange }) => {
  const [wo, setWo] = useState(null);
  const load = () => api.get(`/workorders/${id}`).then((r) => setWo(r.data)).catch(() => setWo(null));
  useEffect(() => {
    if (id) load();
    else setWo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-700 bg-ink-2 sm:max-w-2xl" data-testid="workorder-detail-dialog">
        {wo && (
          <>
            <DialogHeader>
              <p className="font-mono text-xs text-cyan-300">{wo.ref} - {wo.type.toUpperCase()}</p>
              <DialogTitle className="font-display text-2xl uppercase">{wo.title}</DialogTitle>
            </DialogHeader>
            <Stepper stage={wo.stage} />
            <div className="grid gap-2 text-sm sm:grid-cols-2">
              <p><span className="eyebrow">Risk </span><RiskBadge risk={wo.risk} testid="wo-detail-risk" /></p>
              <p><span className="eyebrow">Due </span>{fmtDate(wo.due_date)}</p>
              <p><span className="eyebrow">Location </span>{wo.location || "-"}</p>
              <p><span className="eyebrow">Assigned </span>{wo.assigned_name || "-"}{wo.contractor ? ` / ${wo.contractor}` : ""}</p>
              {wo.asset_name && <p className="sm:col-span-2"><span className="eyebrow">Asset </span>{wo.asset_name}</p>}
            </div>
            <p className="whitespace-pre-line rounded border border-slate-800 bg-slate-900/60 p-3 text-sm text-slate-300">{wo.description || "No description"}</p>
            <EscalationTrail doc={wo} testid="wo-escalation-log" />
            {wo.photos?.length > 0 && <div className="flex flex-wrap gap-2">{wo.photos.map((p) => <Thumb key={p} src={p} />)}</div>}
            <AdvanceForm wo={wo} onDone={() => { load(); onChange?.(); }} />
            <div className="space-y-2">
              <p className="eyebrow">Audit trail</p>
              {wo.history.map((h, i) => (
                <div key={i} className="border-l-2 border-slate-700 pl-3 text-xs">
                  <p className="text-slate-200">{STAGE_LABELS[h.stage]} - {h.by}</p>
                  <p className="text-slate-500">{fmtDateTime(h.at)}{h.note ? ` - ${h.note}` : ""}</p>
                </div>
              ))}
              {wo.verification_signature && <img src={wo.verification_signature} alt="verification signature" className="h-16 rounded bg-white" />}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
